import {
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type IRadioGroupProps from "./type";
import type {
    RadioGroupContextType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    RadioGroupContext
} from "./context";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useStableCallback
} from "../../helpers/interaction";
import {
    useFieldContext,
    FieldContext
} from "../field/context";
import {
    getFieldControlAccessibility,
    getIsFieldOptionList
} from "../field/util";
import {
    warnOnce
} from "../../utils/development";

const RadioGroup: FC<IRadioGroupProps> = ({
    "aria-describedby": ariaDescribedBy,
    "aria-labelledby": ariaLabelledBy,
    orientation = "vertical",
    "aria-label": ariaLabel,
    accessibilityLabel,
    isDisabled = false,
    isRequired = false,
    accessibilityHint,
    value: valueProp,
    isError = false,
    onValueChange,
    defaultValue,
    customTheme,
    children,
    style,
    ...props
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const fieldContext = useFieldContext();

    const [
        valueState,
        setValueState
    ] = useState<string | undefined>(defaultValue);

    const isControlledRef = useRef(valueProp !== undefined);

    const isWeb = Platform.OS === "web";

    const isLabelLinkSupported = isWeb || Platform.OS === "android";

    const isControlled = isControlledRef.current;

    const currentValue = isControlled ? valueProp : valueState;

    const hasOwnLabel = !!ariaLabel || !!accessibilityLabel || !!ariaLabelledBy;

    const {
        accessibilityLabel: fieldAccessibilityLabel,
        accessibilityHint: fieldAccessibilityHint,
        describedBy: fieldDescribedBy,
        labelledBy: fieldLabelledBy,
        isInvalid: isFieldInvalid
    } = getFieldControlAccessibility({
        isLabelled: hasOwnLabel,
        context: fieldContext
    });

    const {
        container: containerDynamicStyle
    } = useStyles({
        isOptionList: getIsFieldOptionList({
            isOptionGroup: true,
            children
        }),
        orientation,
        spaces
    });

    useEffect(() => {
        if(!isControlled || onValueChange) {
            return;
        }

        warnOnce({
            message: "RadioGroup: a controlled \"value\" needs an \"onValueChange\" handler that updates it."
        });
    }, [
        onValueChange,
        isControlled
    ]);

    useEffect(() => {
        if(isControlled === (valueProp !== undefined)) {
            return;
        }

        warnOnce({
            message: "RadioGroup: the group switched between controlled and uncontrolled after the first render. Pass \"value\" on every render, or use \"defaultValue\" only."
        });
    }, [
        isControlled,
        valueProp
    ]);

    const handleSelect = useStableCallback((nextValue: string) => {
        if(isDisabled || nextValue === currentValue) {
            return;
        }

        if(!isControlled) {
            setValueState(nextValue);
        }

        onValueChange?.(nextValue);
    });

    const context = useMemo<RadioGroupContextType>(() => ({
        onSelect: handleSelect,
        value: currentValue,
        isDisabled,
        isError
    }), [
        handleSelect,
        currentValue,
        isDisabled,
        isError
    ]);

    const describedBy = [
        ariaDescribedBy,
        fieldDescribedBy
    ].filter(Boolean).join(" ");

    const currentAccessibilityHint = [
        accessibilityHint,
        fieldAccessibilityHint
    ].filter(Boolean).join(". ") || undefined;

    const webProps = isWeb ? {
        "aria-describedby": describedBy.length ? describedBy : undefined,
        "aria-invalid": isError || isFieldInvalid ? true : undefined,
        "aria-required": isRequired ? true : undefined,
        "aria-orientation": orientation
    } : {};

    return <RadioGroupContext.Provider
        value={context}
    >
        <FieldContext.Provider
            value={undefined}
        >
            <View
                {...props}
                {...webProps}
                aria-labelledby={ariaLabelledBy ?? (isLabelLinkSupported ? fieldLabelledBy : undefined)}
                accessibilityLabel={accessibilityLabel ?? fieldAccessibilityLabel}
                aria-disabled={isDisabled ? true : undefined}
                accessibilityHint={currentAccessibilityHint}
                style={[
                    style,
                    stylesheet.container,
                    containerDynamicStyle
                ]}
                aria-label={ariaLabel}
                role="radiogroup"
            >
                {children}
            </View>
        </FieldContext.Provider>
    </RadioGroupContext.Provider>;
};
export default RadioGroup;
